import * as React from "react";
import { IconPumpkinScary, IconTrophy, IconInfoCircle, IconPlayerPlay, IconRefresh } from "@tabler/icons-react";
import { FlightPreview } from "./FlightPreview.jsx";
import { FlightScene } from "./FlightScene.jsx";
import { FlightRanking } from "./FlightRanking.jsx";
import { FlightRules } from "./FlightRules.jsx";
import { FlightResult } from "./FlightResult.jsx";
import { initialState, VERSION } from "./engine.js";
import { flightRequest, savePending, readPending, clearPending } from "./api.js";
import { syncFlightRequest } from "./flight-sync.js";
import "./halloween.css";

/** @param {{ onBack: () => void, onCurrencyChange?: (balance: number) => void }} props */
export function HalloweenLobby({ onBack, onCurrencyChange }) {
  const [data, setData] = React.useState(null);
  const [view, setView] = React.useState("lobby");
  const [session, setSession] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(true);
  const [paused, setPaused] = React.useState(false);
  const [recovering, setRecovering] = React.useState(false);
  const [error, setError] = React.useState("");
  const [pending, setPending] = React.useState(() => readPending());
  const [clock, setClock] = React.useState(Date.now());
  const mounted = React.useRef(true);
  const confirmed = React.useRef(null);
  const chain = React.useRef(Promise.resolve());
  const pendingRef = React.useRef(pending);
  const latest = React.useRef(null);
  const ended = React.useRef(false);
  const practiceRef = React.useRef(false);
  const bestBeforeStart = React.useRef(0);
  const balanceCallback = React.useRef(onCurrencyChange); balanceCallback.current = onCurrencyChange;
  const serverOffset = React.useRef(0);
  const lifetime = React.useRef(null);
  const recoveryEpoch = React.useRef(0);

  function request(operation, body = {}) {
    const signal = lifetime.current.signal;
    return syncFlightRequest(() => flightRequest(operation, body, { signal }), {
      signal, onRetry: () => { if (!signal.aborted) setRecovering(true); },
    });
  }

  const accept = React.useCallback(value => {
    if (!mounted.current) return;
    if (value.serverTime) serverOffset.current = Date.parse(value.serverTime) - Date.now();
    if (value.attempt) confirmed.current = value.attempt;
    setData(previous => ({ ...previous, daily: value.daily, balance: value.balance, personalBest: value.personalBest, claimed: value.claimed,
      ...(value.leaderboard ? { leaderboard: value.leaderboard } : {}) }));
    if (value.attempt?.status === "finished") { setResult(value.attempt); setView("result"); setSession(null); }
    balanceCallback.current?.(value.balance);
  }, []);

  const refresh = React.useCallback(async () => {
    const signal = lifetime.current.signal;
    setBusy(true); setError("");
    try {
      const value = await request("state");
      if (signal.aborted) return;
      if (mounted.current) {
        setData(value); confirmed.current = value.activeAttempt;
        serverOffset.current = Date.parse(value.serverTime) - Date.now();
        balanceCallback.current?.(value.balance);
      }
      return value;
    } catch (e) { if (!signal.aborted && mounted.current) setError(e.message); }
    finally { if (!signal.aborted && mounted.current) { setBusy(false); setRecovering(false); } }
  }, []);

  React.useEffect(() => {
    mounted.current = true;
    const controller = new AbortController(); lifetime.current = controller;
    chain.current = Promise.resolve();
    async function restoreVisit() {
      const value = await refresh();
      if (!value || controller.signal.aborted) return;
      const saved = pendingRef.current;
      if (saved) await mutate(saved.operation, null, saved.body);
      if (controller.signal.aborted || pendingRef.current) return;
      const current = confirmed.current;
      if (current?.status === "active") {
        if (current.paused) await mutate("resume");
        if (controller.signal.aborted || pendingRef.current || confirmed.current?.status !== "active") return;
        practiceRef.current = false; ended.current = false; latest.current = null;
        bestBeforeStart.current = value.personalBest || 0;
        setSession(confirmed.current); setPaused(false); setView("game");
      }
    }
    restoreVisit();
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => { mounted.current = false; controller.abort(); clearInterval(timer); };
  }, [refresh]);
  React.useEffect(() => { window.scrollTo(0, 0); }, [view]);

  function mutate(operation, packet, explicitBody = null) {
    const signal = lifetime.current.signal;
    const epoch = recoveryEpoch.current;
    // Freeze the queued segment; the canvas can continue while HTTP is in flight.
    const segment = packet ? { state: { ...packet.state }, inputs: [...packet.inputs] } : null;
    const work = async () => {
      if (signal.aborted || epoch !== recoveryEpoch.current || !mounted.current || (pendingRef.current && !explicitBody)) return;
      const current = confirmed.current;
      if (!explicitBody && operation !== "start" && (!current || current.status !== "active")) return;
      if (!explicitBody && operation === "checkpoint" && segment.state.tick <= current.state.tick) return;
      const body = explicitBody || { requestId: crypto.randomUUID(), ...(operation === "start" ? { physicsVersion: VERSION } : { attemptId: current.id }),
        ...(["checkpoint", "finish", "pause"].includes(operation) ? {
          sequence: current.sequence + 1, until: Math.max(current.state.tick, segment.state.tick),
          inputs: segment.inputs.filter(tick => tick > current.state.tick),
        } : {}) };
      const saved = { operation, body }; pendingRef.current = saved; setPending(saved); savePending(saved);
      setBusy(true); setError("");
      try {
        const receipt = await request(operation, body);
        if (signal.aborted) return;
        const value = receipt.current ? { ...receipt, ...receipt.current } : receipt;
        clearPending(body.requestId); pendingRef.current = null;
        if (!mounted.current) return;
        setPending(null); accept(value);
        if (operation === "start" && value.attempt?.status === "active") {
          bestBeforeStart.current = value.personalBest || 0;
          ended.current = false; latest.current = null; practiceRef.current = false;
          setSession(value.attempt); setPaused(false); setView("game");
        }
        if (operation === "resume") setPaused(false);
        if (operation === "pause") setPaused(true);
      } catch (e) {
        if (signal.aborted || !mounted.current) return;
        setError(e.message); setPaused(true);
        if (e.status && e.status < 500 && e.status !== 429) {
          recoveryEpoch.current++;
          clearPending(body.requestId); pendingRef.current = null; setPending(null);
          // A conflicting sequence must rebuild from the server, never replay local claims.
          try {
            const currentState = await request("state");
            if (signal.aborted) return;
            setData(currentState); confirmed.current = currentState.activeAttempt;
            if (currentState.activeAttempt && ["stale_checkpoint", "paused", "active_attempt"].includes(e.code)) {
              const active = currentState.activeAttempt;
              ended.current = false; latest.current = null; practiceRef.current = false;
              setSession({ ...active, recovery: crypto.randomUUID() }); setPaused(active.paused); setView("game"); setError("");
            } else { setSession(null); setView("lobby"); }
          } catch (stateError) { if (!signal.aborted) setError(stateError.message); }
        }
      } finally { if (!signal.aborted && mounted.current) { setBusy(false); setRecovering(false); } }
    };
    chain.current = chain.current.then(work);
    return chain.current;
  }

  function checkpoint(packet) { latest.current = packet; mutate("checkpoint", packet); }
  function finish(packet) {
    latest.current = packet; ended.current = true;
    if (practiceRef.current) {
      setResult({ ...session, state: packet.state, pickups: 0, milestoneReward: 0, practice: true });
      setSession(null); setView("result"); return;
    }
    mutate("finish", packet);
  }
  function pause(packet) { setPaused(true); latest.current = packet; if (!practiceRef.current) mutate("pause", packet); }
  function resume() { if (practiceRef.current) setPaused(false); else mutate("resume"); }
  async function retry() {
    const saved = pendingRef.current;
    if (!saved) { await refresh(); return; }
    await mutate(saved.operation, null, saved.body);
    if (!pendingRef.current && confirmed.current?.status === "active" && latest.current) {
      await mutate(ended.current ? "finish" : "pause", latest.current);
    }
  }
  async function restore() {
    if (pendingRef.current) await retry();
    const current = confirmed.current;
    if (!current || current.status !== "active" || pendingRef.current) return;
    practiceRef.current = false; ended.current = false; latest.current = null;
    bestBeforeStart.current = data?.personalBest || 0;
    if (!current.paused) await mutate("pause", { state: current.state, inputs: [] });
    if (confirmed.current?.status === "active" && !pendingRef.current) { setSession(confirmed.current); setPaused(true); setView("game"); }
  }
  function practice() {
    practiceRef.current = true; ended.current = false;
    setSession({ id: crypto.randomUUID(), state: initialState(), week: { seed: data?.week?.seed || 20261031, version: data?.week?.version ?? VERSION } });
    setPaused(false); setView("game"); setError("");
  }
  async function leaveGame() {
    setPaused(true);
    if (!practiceRef.current && confirmed.current?.status === "active") {
      if (pendingRef.current) await chain.current;
      if (pendingRef.current || !mounted.current) return;
      if (confirmed.current.paused) await mutate("resume");
      await mutate("finish", latest.current || { state: confirmed.current.state, inputs: [] });
      if (pendingRef.current) return;
    }
    setSession(null); setView("lobby"); refresh();
  }
  const back = () => {
    if (view === "game") { leaveGame(); return; }
    if (view !== "lobby") { setView("lobby"); refresh(); return; }
    onBack();
  };
  React.useEffect(() => {
    const button = window.Telegram?.WebApp?.BackButton;
    if (!button) return;
    button.show(); button.onClick(back);
    return () => { button.offClick(back); button.hide(); };
  });

  const emoji = data?.week?.currencyEmoji || "🎃";
  const remaining = data?.week ? Math.max(0, Date.parse(data.week.end) - clock - serverOffset.current) : 0;
  const countdown = `${Math.floor(remaining / 86400000)}д ${Math.floor(remaining / 3600000) % 24}ч ${Math.floor(remaining / 60000) % 60}м`;
  return <section className="halloween-lobby" data-view={view} aria-label="Ночной полёт">
    {error && <div className="nf-error" role="alert"><span>{error}</span><button type="button" onClick={retry} disabled={busy}><IconRefresh />{pending ? "Повторить запрос" : "Обновить"}</button></div>}
    {(pending || recovering) && !error && view !== "game" && <div className="nf-muted" role="status">{recovering ? "Восстанавливаем связь автоматически…" : "Проверяем результат…"}</div>}
    {view === "ranking" ? <FlightRanking initialWeek={data?.week} onBack={back} /> : view === "rules" ? <FlightRules rules={data?.week?.rewards || data?.rules} emoji={emoji} dailyLimit={data?.dailyLimit} pickupReward={data?.pickupReward} onBack={back} /> : view === "game" && session ? <>
      <FlightScene key={`${session.id}:${session.recovery || ""}`} session={session} paused={paused} recovering={recovering} saving={busy && ended.current} daily={data?.daily || 0} dailyLimit={data?.dailyLimit} leaders={data?.leaderboard?.items || []} practice={practiceRef.current}
        onProgress={packet => { latest.current = packet; }} onCheckpoint={practiceRef.current ? () => {} : checkpoint} onFinish={finish} onPause={pause} onResume={resume} onExit={leaveGame} />
    </> : view === "result" && result ? <FlightResult result={result} personalBest={data?.personalBest || 0}
      isNewRecord={result.state.score > bestBeforeStart.current} emoji={emoji} busy={busy || !!pending}
      onRetry={() => result.practice ? practice() : mutate("start")} onRanking={() => setView("ranking")} onBack={back} /> : <>
      <div className="halloween-lobby-card"><span className="halloween-lobby-badge">Недельный турнир</span>
        <FlightPreview />
        <p className="nf-preview-hint">Нажми — взлети · Побей рекорд</p>
        <button type="button" className="nf-primary" disabled={busy || !!pending || (!data?.available && confirmed.current?.status !== "active")} onClick={() => confirmed.current?.status === "active" ? restore() : mutate("start")}><IconPlayerPlay />{busy ? "Загрузка…" : confirmed.current?.status === "active" ? "Продолжить полёт" : "В полёт"}</button>
        {!busy && !data?.available && <small className="nf-muted">Турнир откроется во время активного ивента. Тренировка уже доступна.</small>}
      </div>
      <div className="nf-stat-grid"><div><small>Мой рекорд</small><b>{data?.personalBest || 0}</b><span>очков</span></div><div><small>Моё место</small><b>{data?.leaderboard?.mine ? `#${data.leaderboard.mine.place}` : "—"}</b><span>на этой неделе</span></div></div>
      <div className="nf-milestones" aria-label="Пороги рекорда недели">{(data?.week?.rewards.milestones || [[10, 2], [25, 3], [50, 5], [100, 10]]).map(([goal, amount]) => <span key={goal}><b>{goal} очков</b><small>{data?.claimed?.includes(goal) ? "✓ Получено" : `+${amount} ${emoji}`}</small></span>)}</div>
      <div className="nf-currency"><IconPumpkinScary /><div><b>Собрано сегодня: {data?.daily || 0}/{data?.dailyLimit ?? 20}</b><small>{(data?.daily || 0) >= (data?.dailyLimit ?? 20) ? "Лимит собран · Возвращайся завтра" : `Каждый подбор +${data?.pickupReward ?? 1} ${emoji} · Лимит обновляется ежедневно`}</small></div><span>{emoji} {data?.balance || 0}</span></div>
      <div className="nf-lobby-summary">
        {data?.week && <span>{data.week.closed ? "Итоги в рейтинге" : `До итогов ${countdown}`}</span>}
        {!!data?.leaderboard?.items.length && <span><IconTrophy />Трасса: <b>{data.leaderboard.items[0].score}</b></span>}
      </div>
      <div className="nf-menu"><button type="button" onClick={() => setView("ranking")}><IconTrophy />Рейтинг</button><button type="button" onClick={() => setView("rules")}><IconInfoCircle />Правила</button><button type="button" disabled={!!pending} onClick={practice} aria-label="Тренировка без наград"><IconPlayerPlay />Тренировка</button></div>
    </>}
  </section>;
}
